/**
 * Frontend hooks for the ERP catalog (Apr-2026 ERP v1 PR-A).
 *
 * Most ERP forms need the UoM whitelist to populate dropdowns and the
 * raw-materials list to populate the linked-raw picker. Both are
 * cheap to fetch (small lists) and rarely change, so we let
 * react-query handle caching with a long stale-time and let pages
 * just call the hook without thinking.
 */

import { useQuery } from "@tanstack/react-query";

import {
  type ErpDispatchListFilters,
  type ErpInventoryAdjustmentListFilters,
  type ErpInventoryListFilters,
  type ErpMfgListFilters,
  type ErpOrderListFilters,
  erpApi,
} from "./api";

export const ERP_QUERY_KEYS = {
  uoms: ["erp", "uoms"] as const,
  warehouses: ["erp", "warehouses"] as const,
  warehouse: (id: string) => ["erp", "warehouses", id] as const,
  products: ["erp", "products"] as const,
  product: (id: string) => ["erp", "products", id] as const,
  rawMaterials: ["erp", "raw-materials"] as const,
  rawMaterial: (id: string) => ["erp", "raw-materials", id] as const,
  inventory: (filters: ErpInventoryListFilters) => ["erp", "inventory", filters] as const,
  inventoryAdjustments: (filters: ErpInventoryAdjustmentListFilters) =>
    ["erp", "inventory", "adjustments", filters] as const,
  mfgJobs: (filters: ErpMfgListFilters) => ["erp", "manufacturing-jobs", filters] as const,
  mfgJob: (id: string) => ["erp", "manufacturing-jobs", id] as const,
  drivers: ["erp", "drivers"] as const,
  driver: (id: string) => ["erp", "drivers", id] as const,
  transports: ["erp", "transports"] as const,
  transport: (id: string) => ["erp", "transports", id] as const,
  dispatches: (filters: ErpDispatchListFilters) => ["erp", "dispatches", filters] as const,
  dispatch: (id: string) => ["erp", "dispatches", id] as const,
  lastSold: (accountId: string) => ["erp", "dispatches", "last-sold", accountId] as const,
  orders: (filters: ErpOrderListFilters) => ["erp", "orders", filters] as const,
  order: (id: string) => ["erp", "orders", id] as const,
};

const FIVE_MINUTES_MS = 5 * 60 * 1000;

/** Fetch the canonical UoM list. Cached for 5min — it never changes
 *  inside a session unless we deploy a code change. */
export function useUoms() {
  return useQuery({
    queryKey: ERP_QUERY_KEYS.uoms,
    queryFn: () => erpApi.uoms(),
    staleTime: FIVE_MINUTES_MS,
  });
}

export function useWarehouses() {
  return useQuery({
    queryKey: ERP_QUERY_KEYS.warehouses,
    queryFn: () => erpApi.warehouses.list(),
  });
}

export function useProducts() {
  return useQuery({
    queryKey: ERP_QUERY_KEYS.products,
    queryFn: () => erpApi.products.list(),
  });
}

export function useProduct(id: string | undefined) {
  return useQuery({
    queryKey: id ? ERP_QUERY_KEYS.product(id) : ["erp", "products", "none"],
    queryFn: () => erpApi.products.get(id!),
    enabled: !!id,
  });
}

export function useRawMaterials() {
  return useQuery({
    queryKey: ERP_QUERY_KEYS.rawMaterials,
    queryFn: () => erpApi.rawMaterials.list(),
  });
}

export function useRawMaterial(id: string | undefined) {
  return useQuery({
    queryKey: id ? ERP_QUERY_KEYS.rawMaterial(id) : ["erp", "raw-materials", "none"],
    queryFn: () => erpApi.rawMaterials.get(id!),
    enabled: !!id,
  });
}

export function useInventory(filters: ErpInventoryListFilters = {}) {
  return useQuery({
    queryKey: ERP_QUERY_KEYS.inventory(filters),
    queryFn: () => erpApi.inventory.list(filters),
  });
}

export function useInventoryAdjustments(filters: ErpInventoryAdjustmentListFilters = {}) {
  return useQuery({
    queryKey: ERP_QUERY_KEYS.inventoryAdjustments(filters),
    queryFn: () => erpApi.inventory.listAdjustmentEvents(filters),
  });
}

export function useMfgJobs(filters: ErpMfgListFilters = {}) {
  return useQuery({
    queryKey: ERP_QUERY_KEYS.mfgJobs(filters),
    queryFn: () => erpApi.manufacturing.list(filters),
  });
}

export function useMfgJob(id: string | undefined) {
  return useQuery({
    queryKey: id ? ERP_QUERY_KEYS.mfgJob(id) : ["erp", "manufacturing-jobs", "none"],
    queryFn: () => erpApi.manufacturing.get(id!),
    enabled: !!id,
  });
}

export function useDrivers() {
  return useQuery({
    queryKey: ERP_QUERY_KEYS.drivers,
    queryFn: () => erpApi.drivers.list(),
  });
}

export function useDriver(id: string | undefined) {
  return useQuery({
    queryKey: id ? ERP_QUERY_KEYS.driver(id) : ["erp", "drivers", "none"],
    queryFn: () => erpApi.drivers.get(id!),
    enabled: !!id,
  });
}

export function useTransports() {
  return useQuery({
    queryKey: ERP_QUERY_KEYS.transports,
    queryFn: () => erpApi.transports.list(),
  });
}

export function useTransport(id: string | undefined) {
  return useQuery({
    queryKey: id ? ERP_QUERY_KEYS.transport(id) : ["erp", "transports", "none"],
    queryFn: () => erpApi.transports.get(id!),
    enabled: !!id,
  });
}

export function useDispatches(filters: ErpDispatchListFilters = {}) {
  return useQuery({
    queryKey: ERP_QUERY_KEYS.dispatches(filters),
    queryFn: () => erpApi.dispatches.list(filters),
  });
}

export function useDispatch(id: string | undefined) {
  return useQuery({
    queryKey: id ? ERP_QUERY_KEYS.dispatch(id) : ["erp", "dispatches", "none"],
    queryFn: () => erpApi.dispatches.get(id!),
    enabled: !!id,
  });
}

export function useLastSold(accountId: string | undefined) {
  return useQuery({
    queryKey: accountId
      ? ERP_QUERY_KEYS.lastSold(accountId)
      : ["erp", "dispatches", "last-sold", "none"],
    queryFn: () => erpApi.dispatches.lastSold(accountId!),
    enabled: !!accountId,
  });
}

export function useOrders(filters: ErpOrderListFilters = {}) {
  return useQuery({
    queryKey: ERP_QUERY_KEYS.orders(filters),
    queryFn: () => erpApi.orders.list(filters),
  });
}

export function useOrder(id: string | undefined) {
  return useQuery({
    queryKey: id ? ERP_QUERY_KEYS.order(id) : ["erp", "orders", "none"],
    queryFn: () => erpApi.orders.get(id!),
    enabled: !!id,
  });
}

export function useItemAvailability() {
  return useQuery({
    queryKey: ["erp", "orders", "availability"],
    queryFn: () => erpApi.orders.availability(),
  });
}

/**
 * Group UoM options by category for a `<select>` with `<optgroup>`s.
 * Returns the original list-order within each category.
 */
export function groupUomsByCategory(
  uoms: { code: string; label: string; category: string }[],
): { category: string; items: { code: string; label: string }[] }[] {
  const order: string[] = [];
  const groups = new Map<string, { code: string; label: string }[]>();
  for (const u of uoms) {
    if (!groups.has(u.category)) {
      groups.set(u.category, []);
      order.push(u.category);
    }
    groups.get(u.category)!.push({ code: u.code, label: u.label });
  }
  return order.map((cat) => ({ category: cat, items: groups.get(cat)! }));
}
